import { useCallback, useRef, useState } from 'react';
import type { TrayRefProps } from '@cbhq/cds-web/overlays/tray/Tray';

/**
 * Close guard for a tray with editable state. Closing with unsaved changes shows
 * `UnsavedChangesAlert` instead: "Keep editing" hides the alert, and "Discard"
 * closes the tray.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean) {
  const trayRef = useRef<TrayRefProps>(null);
  const [isAlertVisible, setIsAlertVisible] = useState(false);

  const handleBeforeClose = useCallback(() => {
    if (hasUnsavedChanges) {
      setIsAlertVisible(true);
      return;
    }

    trayRef.current?.close();
  }, [hasUnsavedChanges]);

  const handleKeepEditing = useCallback(() => {
    setIsAlertVisible(false);
  }, []);

  const handleDiscard = useCallback(() => {
    setIsAlertVisible(false);
    trayRef.current?.close();
  }, []);

  return { trayRef, isAlertVisible, handleBeforeClose, handleKeepEditing, handleDiscard };
}
